import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { GenieAvatar } from "@/components/GenieAvatar";
import { Skeleton } from "@/components/ui/skeleton";
import { BuildBandData, ConsoleBand } from "@/types/dashboard/genie-console";
import { resolveGenieBrand } from "../genieBrand";
import { EmptyPreview, PreviewError } from "./previewShell";

interface BuildPreviewProps {
  band: ConsoleBand<BuildBandData>;
  brandColor: string;
  genieId: string | null;
}

/**
 * BUILD band preview — the ACTUAL configuration state of the user's own Genie
 * (its real voice name, its real welcome message, its real counts), plus the
 * other Genies on the account rendered as their real avatars.
 */
export const BuildPreview = ({ band, brandColor, genieId }: BuildPreviewProps) => {
  if (band.isError) return <PreviewError label="setup" />;

  if (band.isLoading) {
    return (
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {[0, 1, 2, 3].map((row) => (
          <Skeleton key={row} className="h-10 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  const { steps, completedCount, totalCount, otherGenies } = band.data;

  if (steps.length === 0) {
    return (
      <EmptyPreview
        message="Once you have a Genie, this is where you'll see what's left to set up."
        actionLabel="Create a Genie"
        to="/user-hub/genie-setup"
        brandColor={brandColor}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${Math.round((completedCount / Math.max(1, totalCount)) * 100)}%`,
            background: brandColor,
          }}
        />
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {steps.map((step) => (
          <div
            key={step.id}
            className="flex items-center gap-2.5 rounded-lg border border-gray-100 bg-white px-3 py-2"
          >
            <span
              className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full"
              style={{
                backgroundColor: step.done ? brandColor : "#F3F4F6",
              }}
            >
              {step.done ? (
                <Check className="h-3 w-3 text-white" aria-hidden="true" />
              ) : (
                <span className="h-1.5 w-1.5 rounded-full bg-gray-400" aria-hidden="true" />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span
                className={`block truncate text-xs font-semibold ${
                  step.done ? "text-gray-900" : "text-gray-500"
                }`}
              >
                {step.label}
              </span>
              {step.detail && (
                <span className="block truncate text-[11px] text-gray-500">{step.detail}</span>
              )}
            </span>
          </div>
        ))}
      </div>

      {otherGenies.length > 0 && (
        <div className="flex items-center gap-2 pt-1">
          <span className="text-xs text-gray-500">Also on your account:</span>
          <div className="flex items-center gap-1.5 overflow-hidden">
            {otherGenies.slice(0, 5).map((genie) => {
              const brand = resolveGenieBrand(genie);
              return (
                <Link
                  key={genie.id}
                  to={`/genie-hub/my-genies/${genie.id}`}
                  title={genie.name}
                  className="flex h-7 w-7 flex-shrink-0 items-center justify-center overflow-hidden rounded-full"
                >
                  <GenieAvatar
                    size={28}
                    color={brand.primary}
                    secondaryColor={brand.secondary}
                    gradientEnabled={brand.gradientEnabled}
                    gradientAngle={brand.gradientAngle}
                    customAvatarUrl={brand.logoUrl}
                    hasCustomBranding={brand.hasCustomBranding}
                  />
                </Link>
              );
            })}
            {otherGenies.length > 5 && (
              <span className="text-xs text-gray-500">+{otherGenies.length - 5}</span>
            )}
          </div>
        </div>
      )}

      {genieId && (
        <Link
          to={`/genie-hub/my-genies/${genieId}`}
          className="inline-flex text-xs font-semibold hover:underline"
          style={{ color: brandColor }}
        >
          Finish setting this Genie up
        </Link>
      )}
    </div>
  );
};

export default BuildPreview;
